import * as signalR from '@microsoft/signalr'

const BASE_URL = process.env.REACT_APP_API_URL ?? ''

let connection: signalR.HubConnection | null = null
let lastToken:  string | null = null   // invoke 자동 reconnect용

// ── 연결 생성 / 반환 ──────────────────────────────────────
export function getConnection(token: string): signalR.HubConnection {
  lastToken = token
  if (connection && connection.state !== signalR.HubConnectionState.Disconnected) {
    return connection
  }

  connection = new signalR.HubConnectionBuilder()
    .withUrl(`${BASE_URL}/Hub/Catan?access_token=${encodeURIComponent(token)}`)
    .withAutomaticReconnect()
    .configureLogging(signalR.LogLevel.Warning)
    .build()

  return connection
}

// ── 연결 시작 ────────────────────────────────────────────
export async function startConnection(token: string): Promise<void> {
  lastToken = token
  const conn = getConnection(token)
  if (conn.state === signalR.HubConnectionState.Disconnected) {
    await conn.start()
  }
}

// ── 연결 보장 (invoke 호출 전 안전망) ─────────────────────
async function ensureConnected(): Promise<signalR.HubConnection> {
  if (!connection || connection.state === signalR.HubConnectionState.Disconnected) {
    if (!lastToken) throw new Error('CatanHub 인증 토큰 없음 — 재로그인 필요')
    await startConnection(lastToken)
  }
  return connection!
}

// ── 연결 종료 ────────────────────────────────────────────
export async function stopConnection(): Promise<void> {
  if (connection) {
    await connection.stop()
    connection = null
  }
}

// ── 이벤트 리스너 등록 헬퍼 (서버 → 클라) ──────────────────
type HubCallback = (...args: unknown[]) => void

export function on(event: string, callback: HubCallback): void {
  if (!connection) return
  connection.on(event, callback)
}

export function off(event: string, callback: HubCallback): void {
  if (!connection) return
  connection.off(event, callback)
}

// ── invoke 헬퍼 (자동 reconnect, Phase 3+ 게임 액션에서 활용) ─────────
export async function invoke(method: string, ...args: unknown[]): Promise<unknown> {
  const conn = await ensureConnected()
  return conn.invoke(method, ...args)
}

// ── 연결 상태 ─────────────────────────────────────────────
export function isConnected(): boolean {
  return connection?.state === signalR.HubConnectionState.Connected
}

export function getState(): signalR.HubConnectionState | null {
  return connection?.state ?? null
}

// ── Phase 3 게임 액션 invoke 헬퍼 ─────────────────────────

/** SETUP 마을 배치 */
export async function placeInitialSettlement(matchId: number, vertexId: number): Promise<void> {
  await invoke('PlaceInitialSettlement', matchId, vertexId)
}

/** SETUP 도로 배치 */
export async function placeInitialRoad(matchId: number, edgeId: number): Promise<void> {
  await invoke('PlaceInitialRoad', matchId, edgeId)
}

// ── Phase 4-1 게임 액션 invoke 헬퍼 ───────────────────────

/** 본 턴 주사위 굴림 (MAIN_TURN_PREROLL 상태에서만 허용) */
export async function rollDice(matchId: number): Promise<void> {
  await invoke('RollDice', matchId)
}

// ── Phase 4-2 게임 액션 invoke 헬퍼 ───────────────────────

/**
 * 카드 폐기 (7 굴림 후 8장+ 보유자 절반 폐기)
 * 5종 자원 갯수를 합쳐 floor(보유/2) 와 정확히 일치해야 함.
 */
export async function discardCards(
  matchId: number, wool: number, wheat: number, brick: number, ore: number, wood: number,
): Promise<void> {
  await invoke('DiscardCards', matchId, wool, wheat, brick, ore, wood)
}

/**
 * 도적 이동 + 약탈
 * @param targetHexId 1~19, 현재 도적 위치 외 헥스
 * @param victimUserCode 약탈 대상. 인접 마을/도시 보유자 중 1명. null=약탈 안 함
 */
export async function moveRobber(
  matchId: number, targetHexId: number, victimUserCode: number | null,
): Promise<void> {
  await invoke('MoveRobber', matchId, targetHexId, victimUserCode)
}

// ── Phase 4-3 게임 액션 invoke 헬퍼 (건설 3종) ─────────────

/** 본 턴 도로 건설 (MAIN_TURN_POSTROLL, 자원 나무1+벽돌1, 본인 도로/마을/도시 인접) */
export async function buildRoad(matchId: number, edgeId: number): Promise<void> {
  await invoke('BuildRoad', matchId, edgeId)
}

/** 본 턴 마을 건설 (MAIN_TURN_POSTROLL, 자원 나무1+벽돌1+양1+밀1, 2칸 거리, 본인 도로 인접) */
export async function buildSettlement(matchId: number, vertexId: number): Promise<void> {
  await invoke('BuildSettlement', matchId, vertexId)
}

/** 본 턴 도시 업그레이드 (MAIN_TURN_POSTROLL, 자원 광물3+밀2, 본인 마을 위치) */
export async function buildCity(matchId: number, vertexId: number): Promise<void> {
  await invoke('BuildCity', matchId, vertexId)
}

// ── Phase 4-4 게임 액션 invoke 헬퍼 ───────────────────────

/** 턴 종료 (MAIN_TURN_POSTROLL 상태에서만 허용) */
export async function endTurn(matchId: number): Promise<void> {
  await invoke('EndTurn', matchId)
}

// ── SEQ=10112 P8-CK-A — EXT_CK 도시 개선 invoke 헬퍼 ─────────────
/**
 * 도시 개선 (EXT_CK ON 룸 전용)
 * @param category 'POL' (정치, COIN) | 'SCI' (과학, PAPER) | 'TRD' (상업, CLOTH)
 * 비용: 다음 단계 번호만큼 해당 상품 1~5 누적
 */
export async function improveCity(matchId: number, category: 'POL' | 'SCI' | 'TRD'): Promise<void> {
  await invoke('ImproveCity', matchId, category)
}

// ── SEQ=10113 P8-CK-B — EXT_CK 기사 시스템 invoke 헬퍼 (5종) ─────
/** Basic Knight 건설 (자기 도로 인접 vertex, WOOL 1 + ORE 1) */
export async function buildKnight(matchId: number, vertexId: number): Promise<void> {
  await invoke('BuildKnight', matchId, vertexId)
}
/** Knight 격상 (LEVEL +1, max 3, WOOL 1 + ORE 1) */
export async function upgradeKnight(matchId: number, knightId: number): Promise<void> {
  await invoke('UpgradeKnight', matchId, knightId)
}
/** Knight 활성화 (WHEAT 1) */
export async function activateKnight(matchId: number, knightId: number): Promise<void> {
  await invoke('ActivateKnight', matchId, knightId)
}
/** Active Knight 이동 (도로 경로 1칸, 적 약한 기사 추방 가능) */
export async function moveKnight(matchId: number, knightId: number, newVertexId: number, pathEdgeId: number): Promise<void> {
  await invoke('MoveKnight', matchId, knightId, newVertexId, pathEdgeId)
}
/** Active Knight로 도적 추방 (도적 인접 vertex 위치 기사만) */
export async function chaseRobber(matchId: number, knightId: number, knightVertexId: number, newRobberHexId: number): Promise<void> {
  await invoke('ChaseRobber', matchId, knightId, knightVertexId, newRobberHexId)
}

// ── SEQ=10114 P8-CK-C — EXT_CK 야만인 트랙 + 도시 성벽 invoke 헬퍼 ─────
/** 야만인 트랙 +1 명시 호출 (RollDice 12 시 자동 hook과 별개, 디버그·진보 카드용) */
export async function barbarianAdvance(matchId: number): Promise<void> {
  await invoke('BarbarianAdvance', matchId)
}
/** 도시 성벽 건설 (본인 도시 vertex, BRICK 2) */
export async function buildCityWall(matchId: number, vertexId: number): Promise<void> {
  await invoke('BuildCityWall', matchId, vertexId)
}

// ── SEQ=10115 P8-CK-D — 진보 카드 75장 invoke 헬퍼 ─────
/** 진보 카드 추첨 (해당 분야 도시개선 1+ 보유자만, 카테고리: POL/SCI/TRD) */
export async function drawProgressCard(matchId: number, category: 'POL' | 'SCI' | 'TRD'): Promise<void> {
  await invoke('DrawProgressCard', matchId, category)
}
/** 진보 카드 사용 (본인 핸드 카드, payloadJson은 효과별 옵션 — null 가능) */
export async function useProgressCard(matchId: number, cardId: number, payloadJson?: string | null): Promise<void> {
  await invoke('UseProgressCard', matchId, cardId, payloadJson ?? null)
}

// SEQ=10125 P8-CK-H2 (c) — POL_SPY second-stage RPC
// Spy 카드 USED 영수증 검증 + 1회 한정 (server SP). 결과는 Caller 에 SpyHandRevealed 이벤트로.
export async function spyGetHand(matchId: number, targetUserCode: number): Promise<void> {
  await invoke('SpyGetHand', matchId, targetUserCode)
}

// ── Phase 6 발전 카드 invoke 헬퍼 (구매 + 사용 5종 + 조회) ─────

/** 본인 보유 카드 + 덱 잔량 + 기사단 승점 보유자 조회 */
export async function getMyDevCards(matchId: number): Promise<void> {
  await invoke('GetMyDevCards', matchId)
}

/** 발전 카드 1장 구매 (양 1 + 곡식 1 + 광물 1, MAIN_TURN_POSTROLL) */
export async function buyDevCard(matchId: number): Promise<void> {
  await invoke('BuyDevCard', matchId)
}

/**
 * 기사 카드 사용 (도적 이동 + 약탈)
 * @param robberTargetHex 1~19, 도적이 이동할 hex
 * @param victimUserCode 약탈 대상 USER_CODE (null=인접 마을 없음 또는 약탈 안 함)
 */
export async function useKnight(
  matchId: number, cardId: number, robberTargetHex: number, victimUserCode: number | null
): Promise<void> {
  await invoke('UseKnight', matchId, cardId, robberTargetHex, victimUserCode)
}

/** 도로 건설 카드 사용 (서로 다른 edge 2개 무료 건설) */
export async function useRoadBuilding(
  matchId: number, cardId: number, edgeId1: number, edgeId2: number
): Promise<void> {
  await invoke('UseRoadBuilding', matchId, cardId, edgeId1, edgeId2)
}

/** 독점 카드 사용 (자원 1종 선택 → 다른 모든 플레이어로부터 그 자원 약탈) */
export async function useMonopoly(
  matchId: number, cardId: number, resourceType: string
): Promise<void> {
  await invoke('UseMonopoly', matchId, cardId, resourceType)
}

/** 풍년 카드 사용 (은행 자원 2장 자유 선택, 같은 자원 2장도 가능) */
export async function useYearOfPlenty(
  matchId: number, cardId: number, resource1: string, resource2: string
): Promise<void> {
  await invoke('UseYearOfPlenty', matchId, cardId, resource1, resource2)
}

/** 승점 카드 즉시 공개 (점수는 구매 시 이미 +1 반영, 본 액션은 공개 표기만) */
export async function useVictoryPoint(matchId: number, cardId: number): Promise<void> {
  await invoke('UseVictoryPoint', matchId, cardId)
}

// ── 단계 5 (Phase 7) invoke 헬퍼 ─────────────────────────

/** 모든 플레이어 점수 + 본인 총 승점 동기화 (진입/재연결 시) */
export async function getAllScores(matchId: number): Promise<void> {
  await invoke('GetAllScores', matchId)
}

// ── Phase 5 거래 invoke 헬퍼 (5종) ────────────────────────

/**
 * 거래 제안 (MAIN_TURN_POSTROLL 본인 턴, 본인 OPEN 1건 제약)
 * giveJson/getJson: 5종 자원 키만 양수 — `{"WOOD":1,"BRICK":2}` 형식
 * targetUserCode: null=전체 / 값=특정 1명 (D-3)
 */
export async function proposeTrade(
  matchId: number, giveJson: string, getJson: string, targetUserCode: number | null,
): Promise<void> {
  await invoke('ProposeTrade', matchId, giveJson, getJson, targetUserCode)
}

/**
 * 거래 응답 — ACCEPT / REJECT / COUNTER
 * COUNTER 시 counterGiveJson/counterGetJson 필수, 카운터에 카운터는 거부 (D-1·D-11)
 */
export async function respondTrade(
  matchId: number, tradeId: number, action: 'ACCEPT' | 'REJECT' | 'COUNTER',
  counterGiveJson: string | null, counterGetJson: string | null,
): Promise<void> {
  await invoke('RespondTrade', matchId, tradeId, action, counterGiveJson, counterGetJson)
}

/** 거래 취소 (제안자 본인 OPEN만) */
export async function cancelTrade(matchId: number, tradeId: number): Promise<void> {
  await invoke('CancelTrade', matchId, tradeId)
}

/** 은행 4:1 거래 (협상 없음, 즉시 처리) */
export async function bankTrade(
  matchId: number, giveType: string, getType: string,
): Promise<void> {
  await invoke('BankTrade', matchId, giveType, getType)
}

/** 항구 2:1·3:1 거래 (본인 권한 항구만, RATIO=2면 자원 종류 일치 필요) */
export async function portTrade(
  matchId: number, portId: number, giveType: string, getType: string,
): Promise<void> {
  await invoke('PortTrade', matchId, portId, giveType, getType)
}
